import { Token } from 'prismjs';
import { Path } from 'slate';
import { AulaRange } from '../../types';

/**
 * A prism token can be a string, a token or an array of tokens. To translate to Slate
 * ranges we need to be able to calculate the length of content that each token refers to.
 */
const getRangeLength = (token: string | Token): number => {
  // A string from the syntax, won't have any styling applied.
  if (typeof token === 'string') {
    return token.length;
  }

  // A token from the syntax that needs to be decorated.
  if (typeof token.content === 'string') {
    return token.content.length;
  }

  // Tokens can be nested, we need the aggregated length of the entire token
  if (Array.isArray(token.content)) {
    return token.content.reduce(
      (totalLength: number, text) => totalLength + getRangeLength(text),
      0
    );
  }

  // In practice a token shouldn't have a single token as it's token but the types say it's possible
  return getRangeLength(token.content);
};

/**
 * Given an array of tokens, converts them to Slate ranges. This is recursive since a token can
 * actually be a range of tokens, a good example is JS template strings
 */
export const getDecoratedRanges = (
  path: Path,
  tokens: (string | Token)[],
  ranges: AulaRange[] = [],
  start = 0
): AulaRange[] => {
  if (!tokens.length) {
    return ranges;
  }

  const additionalRanges = [];
  let currentLocation = start;

  for (const token of tokens) {
    const length = getRangeLength(token);
    const end = currentLocation + length;

    // If the token is a string there's no decoration required, however we still need to account for
    // it's length below so that we can calcuate ranges for subsequent tokens.
    if (typeof token !== 'string') {
      // Array of tokens: get the ranges for each token in the array
      if (Array.isArray(token.content)) {
        additionalRanges.push(
          ...getDecoratedRanges(path, token.content, ranges, currentLocation)
        );
      } else {
        // Token: create a range for decoration
        additionalRanges.push({
          codeToken: token.type,
          anchor: { path, offset: currentLocation },
          focus: { path, offset: end },
        });
      }
    }

    currentLocation += length;
  }

  return [...ranges, ...additionalRanges];
};
